/**
 * CoachMark — dismissable tooltip overlay for first-time gesture hints.
 */

import React, { useId, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal } from 'react-native';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { FONT_FAMILY, FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';
import { useBackHandler } from '../../navigation/useBackHandler';

interface Props {
  visible: boolean;
  text: string;
  onDismiss: () => void;
}

export default function CoachMark({ visible, text, onDismiss }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const modalId = useId();

  useBackHandler({
    id: `coach-mark-${modalId}`,
    label: 'Coach mark',
    priority: 950,
    enabled: visible,
    onBack: () => {
      onDismiss();
      return true;
    },
  });

  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onDismiss}>
      <TouchableOpacity style={s.overlay} activeOpacity={1} onPress={onDismiss} accessibilityRole="button" accessibilityLabel="Dismiss hint">
        <View style={s.bubble}>
          <Text style={s.text}>{text}</Text>
          <Text style={s.dismiss}>Tap to dismiss</Text>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: SPACING.xl,
    },
    bubble: {
      backgroundColor: colors.primary,
      paddingVertical: SPACING.base,
      paddingHorizontal: SPACING.xl,
      borderRadius: RADIUS.lg,
      maxWidth: 300,
      alignItems: 'center',
    },
    text: {
      fontFamily: FONT_FAMILY.bodySemibold,
      fontSize: FONT_SIZE.base,
      color: '#FFFFFF',
      textAlign: 'center',
      marginBottom: SPACING.xs,
    },
    dismiss: {
      fontFamily: FONT_FAMILY.body,
      fontSize: FONT_SIZE.xs,
      color: 'rgba(255,255,255,0.7)',
    },
  });
}
